// ABOUTME: Photo analysis utilities for SmartQuoteAI to detect problems from images
// ABOUTME: Leverages existing OCR functionality and adds visual problem detection

import { performOCR } from '@/components/QuickAdd/imageUtils';
import { analyzeUserInput, TradeMatch } from './keywordMatcher';
import { debug } from '@/lib/utils';

export interface PhotoAnalysisResult {
  extractedText: string;
  detectedProblems: string[];
  suggestedTrades: TradeMatch[];
  confidence: number;
  analysis: string;
}

/**
 * Common visual indicators that suggest specific trade problems
 */
const VISUAL_PROBLEM_PATTERNS = {
  plumbing: [
    'water', 'wet', 'damp', 'stain', 'drip', 'leak', 'pipe', 'tap', 'toilet',
    'sink', 'bath', 'shower', 'drain', 'overflow', 'flood'
  ],
  electrical: [
    'wire', 'cable', 'socket', 'switch', 'light', 'electrical', 'power',
    'sparking', 'burn', 'scorch', 'blackened', 'outlet', 'breaker'
  ],
  heating: [
    'boiler', 'radiator', 'thermostat', 'heating', 'cold', 'no heat',
    'gas', 'pilot light', 'flame', 'temperature'
  ],
  roofing: [
    'roof', 'tile', 'slate', 'gutter', 'downpipe', 'leak', 'hole',
    'missing', 'damaged', 'storm damage', 'ceiling stain'
  ],
  building: [
    'crack', 'hole', 'damage', 'broken', 'wall', 'ceiling', 'floor',
    'brick', 'plaster', 'paint', 'door', 'window', 'frame'
  ],
  gardening: [
    'garden', 'lawn', 'tree', 'grass', 'hedge', 'fence', 'overgrown',
    'weeds', 'landscaping', 'patio', 'decking'
  ]
};

/**
 * Analyze a photo to detect potential trade problems
 */
export async function analyzePhoto(imageFile: File): Promise<PhotoAnalysisResult> {
  debug.log('Starting photo analysis', {
    fileName: imageFile.name,
    fileSize: imageFile.size,
    fileType: imageFile.type
  });

  try {
    // Extract text from the image using existing OCR
    const extractedText = await performOCR(imageFile, 15000); // 15 second timeout

    debug.log('OCR extraction completed', {
      textLength: extractedText.length,
      hasText: extractedText.length > 0
    });

    // Analyze the extracted text for trade-related keywords
    const textAnalysis = analyzeUserInput(extractedText);

    // Detect visual problem indicators
    const detectedProblems = detectVisualProblems(extractedText);

    // Calculate confidence based on text quality and matches
    const confidence = calculatePhotoConfidence(extractedText, textAnalysis.matches, detectedProblems);

    // Generate analysis summary
    const analysis = generatePhotoAnalysis(extractedText, textAnalysis.matches, detectedProblems);

    const result: PhotoAnalysisResult = {
      extractedText,
      detectedProblems,
      suggestedTrades: textAnalysis.matches,
      confidence,
      analysis
    };

    debug.log('Photo analysis completed', {
      problemsDetected: detectedProblems.length,
      tradesMatched: textAnalysis.matches.length,
      confidence,
      topTrade: textAnalysis.topMatch?.category.displayName
    });

    return result;

  } catch (error) {
    debug.error('Photo analysis failed:', error);

    // Return empty result on failure
    return {
      extractedText: '',
      detectedProblems: [],
      suggestedTrades: [],
      confidence: 0,
      analysis: 'Could not analyze photo. Please describe your problem in text instead.'
    };
  }
}

/**
 * Detect visual problem indicators from extracted text
 */
function detectVisualProblems(text: string): string[] {
  if (!text || text.trim().length === 0) {
    return [];
  }

  const lowerText = text.toLowerCase();
  const detectedProblems: string[] = [];

  // Check for each problem category
  Object.entries(VISUAL_PROBLEM_PATTERNS).forEach(([category, patterns]) => {
    const matchedPatterns = patterns.filter(pattern => lowerText.includes(pattern));

    if (matchedPatterns.length > 0) {
      detectedProblems.push(`${category}: ${matchedPatterns.join(', ')}`);
    }
  });

  return detectedProblems;
}

/**
 * Calculate confidence score for photo analysis
 */
function calculatePhotoConfidence(
  extractedText: string,
  tradeMatches: TradeMatch[],
  problems: string[]
): number {
  let confidence = 0;

  // Base confidence from text extraction
  if (extractedText.length > 10) confidence += 0.2;
  if (extractedText.length > 50) confidence += 0.2;

  // Confidence from trade matches
  if (tradeMatches.length > 0) {
    confidence += 0.3;
    // Bonus for high-confidence matches
    if (tradeMatches[0]?.confidence > 0.7) confidence += 0.2;
  }

  // Confidence from problem detection
  if (problems.length > 0) confidence += 0.3;

  return Math.min(confidence, 1.0);
}

/**
 * Generate human-readable analysis of the photo
 */
function generatePhotoAnalysis(
  extractedText: string,
  tradeMatches: TradeMatch[],
  problems: string[]
): string {
  const insights: string[] = [];

  if (extractedText.length === 0) {
    return "I couldn't extract any text from this image. Try describing your problem instead.";
  }

  if (tradeMatches.length > 0) {
    const topMatch = tradeMatches[0];
    insights.push(`This looks like a job for a ${topMatch.category.displayName.toLowerCase()}`);
  }

  if (problems.length > 0) {
    insights.push(`I can see indicators of: ${problems.join(', ')}`);
  }

  if (extractedText.length > 20) {
    insights.push(`Found text: "${extractedText.substring(0, 100)}${extractedText.length > 100 ? '...' : ''}"`);
  }

  if (insights.length === 0) {
    return "I can see some text in the image, but couldn't identify specific problems. Try describing what you need help with.";
  }

  return insights.join('. ');
}

/**
 * Check if device supports photo capture
 */
export function isPhotoCaptureSupported(): boolean {
  // Check for camera API support
  const hasGetUserMedia = !!(navigator.mediaDevices?.getUserMedia);

  // Check for file input support
  const hasFileAPI = !!(window.File && window.FileReader);

  return hasGetUserMedia && hasFileAPI;
}

/**
 * Generate suggestions based on photo analysis
 */
export function generatePhotoSuggestions(analysis: PhotoAnalysisResult): string[] {
  const suggestions: string[] = [];

  if (analysis.confidence < 0.3) {
    suggestions.push("Try taking a clearer photo with better lighting");
    suggestions.push("Make sure any text or labels are clearly visible");
    suggestions.push("Consider describing the problem in text instead");
  }

  if (analysis.suggestedTrades.length === 0) {
    suggestions.push("Describe what type of work you need help with");
    suggestions.push("Mention the location (kitchen, bathroom, garden, etc.)");
  }

  if (analysis.extractedText.length > 0 && analysis.suggestedTrades.length === 0) {
    suggestions.push("Add more details about what needs fixing or installing");
  }

  return suggestions;
}
